import { gameState } from "appworks/model/game-state";
import { Model } from "appworks/model/model";
import { RealityMode } from "appworks/model/settings/settings-model";
import { Connector } from "appworks/server/connectors/connector";
import { SocketConnector, SocketRequest } from "appworks/server/connectors/socket-connector";
import { Services } from "appworks/services/services";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { AgsAuthResponseSchema } from "../schema/ags-auth-response-schema";
import { GameiomPlatformSchema } from "gameiom/schema/gameiom-platform-schema";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";

export interface AgsErrorResponseSchema {
    error: AgsError;
    platform: GameiomPlatformSchema;
}

export interface AgsError {
    code: number;
    message: string;
}

class AgsSocketConnector extends SocketConnector implements Connector {

    public clearUser() {
        const { playMode } = Model.read().settings;
        const body = {
            platform: {
                sessionToken: gameiomModel.sessionToken,
                username: gameiomModel.username,
                providerId: gameiomModel.providerId,
                operatorId: gameiomModel.operatorId,
                gameId: gameiomModel.gameId
            },
            publicState: {
                clearState: true,
                type: "open",
                userName: gameiomModel.username,
                mode: playMode === RealityMode.REAL ? "realPlay" : "freePlay",
                jurisdiction: gameiomModel.jurisdiction,
                brandId: "sugar",
                engine: "rsi-blackjack",
                gameType: "side-bets"
            }
        };

        this.sendRequest(new SocketRequest(body, gameiomModel.url)).execute();
    }

    protected resolveRequest(message: any): void {
        const errorResponse = JSON.parse(message.data) as AgsErrorResponseSchema;

        if (errorResponse.error !== undefined) {
            let fatal = true;
            let errorMsg = errorResponse.error.message;

            if (errorResponse.error.code?.toString() === "1003") {
                fatal = false;
                errorMsg = "insufficient_funds";

                const currGame = gameState.getCurrentGame();
                const multiplier = gameiomModel.isSocial ? 1 : 100;
                const realBalance = (errorResponse?.platform?.balance ?? (currGame.realBalance || currGame.balance)) * multiplier;
                const freeBalance = (errorResponse?.platform?.balanceDetails?.bonus ?? 0) * multiplier;

                gameWrapper.balancesUpdate(
                    realBalance,
                    freeBalance
                );
                gameWrapper.balancesUpdate(
                    realBalance,
                    freeBalance
                );
                gameWrapper.insufficientFunds();
            }

            this.errorSignal.dispatch(errorMsg, fatal);

            if (!fatal) {
                super.resolveRequest(message);
            } else {
                this.close();

                const data = JSON.parse(message.data);
                if (data?.publicState?.action === "init") {
                    this.simpleAlert(errorResponse.error.message);
                }
            }
        } else {
            super.resolveRequest(message);
            const isResponsibleGamingEvent = (errorResponse as unknown as AgsAuthResponseSchema).platform?.responsibleGamingEvent ?? false;
            const currentGame = gameState.getCurrentGame();

            if (!isResponsibleGamingEvent && currentGame.hasRecords()) {
                const isFreespins = currentGame.inFreespins();

                if(!isFreespins) {
                    gameWrapper.balancesUpdate(
                        currentGame.realBalance,
                        currentGame.freeBalance,
                        currentGame.getTotalCurrentSpinWin()
                    );
                    gameWrapper.balancesUpdate(
                        currentGame.realBalance,
                        currentGame.freeBalance,
                    );
                }
            }
        }
    }

    protected simpleAlert(message: string) {
        const translations = Services.get(TranslationsService);
        message = translations?.exists(message) ? translations.get(message) : message;
        message = message.trim() === "" ? "Fatal Error" : message;

        const alertEl = document.createElement("div");
        alertEl.id = "simple-alert";
        alertEl.classList.add("simple-alert");

        const alertElWrapper = document.createElement("div");
        alertElWrapper.classList.add("simple-alert__wrapper");
        alertEl.appendChild(alertElWrapper);

        const titleEl = document.createElement("div");
        titleEl.classList.add("simple-alert__title");
        titleEl.innerText = "Error";
        alertElWrapper.appendChild(titleEl);

        const messageEl = document.createElement("div");
        messageEl.classList.add("simple-alert__message");
        messageEl.innerText = translations.get(message);

        alertElWrapper.appendChild(messageEl);

        const btnEl = document.createElement("a");
        btnEl.classList.add("simple-alert__button");
        btnEl.innerText = translations.get("OK");
        btnEl.href = document.location.href;
        alertElWrapper.appendChild(btnEl);

        document.body.appendChild(alertEl);
    }
}

export const agsSocketConnector = new AgsSocketConnector();
